import React from "react";
import { toast } from "react-toastify";
import BackArrowButton from "./BackArrowButton";
import { ShareUpIcon } from "./icons";
import { shareLink } from "../../../utils/shareLink";
import { leagueShareData, leagueShareUrl } from "../../../utils/shareMessage";

// THE DESKTOP HEADER ROW for every League Play landing page.
//
// Client: "every League Play screen uses one shared header. Left: back arrow.
// Center: QUSHi logo. Right: share icon — landing pages only. Nothing else in
// the header bar. The three screens differ in their body content, never in
// header structure."
//
// On a phone that header IS the app's own fixed bar — arrow, logo, share — and
// the client marked it correct as-is ("This is the template"). Desktop is the
// one structural difference they allow: "Do NOT move the centered QUSHi logo
// down from the header — desktop keeps the logo in the top header… remove the
// 'All Contests' text label. Keep the back arrow, aligned to the left edge of
// the content column. Add the share icon aligned to the right edge of the
// content column. The arrow and share icon bracket the content area."
//
// So this is that bracket, and it is desktop-only. The label is gone: an arrow
// is the site's own back control everywhere else and does not need naming.
//
// `showShare` is false on the leaderboard preview — "No share icon —
// leaderboards are not shareable yet. Share is landing-pages-only for now" —
// and on a private league, whose link is not ours to hand out (the same rule
// PublicLeagueJoinCard's own share button already followed).
const LeaguePlayTopBar = ({
  game,
  showShare = true,
  fallback = "/contest",
  className = "mb-6",
}) => {
  const share = async () => {
    // The wording is the client's, built in utils/shareMessage so all three
    // competition share controls send the same thing — leagueShareData picks
    // public, private or cash-game phrasing off the league itself.
    const result = await shareLink(leagueShareData(game, leagueShareUrl(game)));
    // "shared" means the OS sheet handled it and is its own confirmation.
    if (result === "copied") toast.success("League link copied");
    else if (result === "failed") toast.error("Could not copy the link");
  };

  return (
    <div className={`hidden md:flex items-center justify-between ${className}`}>
      {/* The site-standard arrow. Client: "use the site-standard arrow… Use it
          on every League Play screen, same style/color, same top-left position.
          Do not substitute any other arrow." BackArrowButton is that arrow, and
          it is already what the leaderboard preview and the league profile
          render — so this row adopts it rather than drawing a third one. */}
      <BackArrowButton fallback={fallback} className="!mb-0" />

      {showShare ? (
        <button
          type="button"
          onClick={share}
          aria-label="Share this league"
          className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-secondary transition-colors hover:bg-[#F1F5F5] hover:text-[#0F4146]"
        >
          <ShareUpIcon className="h-5 w-5" />
        </button>
      ) : (
        // Keeps the arrow hard against the column's left edge whether or not the
        // row has a second control, rather than letting `justify-between`
        // centre a lone child.
        <span aria-hidden="true" />
      )}
    </div>
  );
};

export default LeaguePlayTopBar;
